const ErrorUtil = require("../../../shared/ErrorUtil");
const {setIn} = require("../../../shared/ImmHelpers");

// Functions to deal with timer objects in the vault. These track the time the
// user is spending on something, and it accounts for things like undetected
// sleeping. Treat the structure of this object as opaque and use the getters
// to retrieve things.

// Internal helper for updating amountMs.
const updateAmount = function(timer, clock) {
	if (!clock || timer.paused) {
		return timer.amountMs;
	}
	let increment = clock.getActiveSince(timer.lastUpdateMs);
	const maxIncrement = clock.getMaxIncrement();
	if (increment > maxIncrement) {
		const message =
			"Tried to increment timer by " +
			increment +
			"ms, " +
			"above idle cap of " +
			maxIncrement +
			"ms.";
		ErrorUtil.log("E_TIMER_JUMP", message, {
			increment,
			maxIncrement,
			now: clock.now(),
			currTimerStart: timer.startMs,
			currTimerLastUpdate: timer.lastUpdateMs,
			currTimerAmount: timer.amountMs,
			currIdle: clock.getIdleDuration(),
		});
		increment = maxIncrement;
	}
	//Make sure increments are at least 0.
	increment = Math.max(0, increment);
	return timer.amountMs + increment;
};

// Internal helper to update a timer object. clock is optional.
const updateHelper = function(timer, clock, options = {}) {
	const {paused = timer.paused} = options;
	const {startMs, lastUpdateMs, amountMs} = timer;
	return {
		startMs: clock && startMs === null && !paused ? clock.now() : startMs,
		lastUpdateMs: clock ? clock.now() : lastUpdateMs,
		amountMs: clock ? updateAmount(timer, clock) : amountMs,
		paused,
	};
};

//
// Public modifiers that return new/changed timer objects.
//

// Start a new timer which is initially unpaused.
const init = function(clock, initAmountMs = 0) {
	if (!clock) {
		throw new Error("OpsTimer.init requires the clock parameter");
	}
	return {
		startMs: clock.now(),
		lastUpdateMs: clock.now(),
		amountMs: initAmountMs,
		paused: false,
	};
};

// Start a new timer that is paused, meaning it can be passed to
// resume/pause/update and have the right thing happen. Its startMs value will
// be the first time the timer was resumed. The clock parameter is ignored, but
// included in the signature for consistency with init; it is okay to make null.
const initPaused = function(clock, initAmountMs = 0) {
	return {
		startMs: null,
		lastUpdateMs: 0,
		amountMs: initAmountMs,
		paused: true,
	};
};

// Database stores time in seconds, we need to convert to ms before initPaused.
const initPausedFromDBTimeSpent = function(clock, timeSpent) {
	const msTime = parseFloat(timeSpent * 1000 || 0);
	return initPaused(clock, msTime);
};

// Resume an existing timer. If timer was already unpaused, this is equivalent
// to update. You must pass clock to this function, or it will throw.
const resume = function(timer, clock) {
	if (!clock) {
		throw new Error("OpsTimer.resume requires the clock parameter");
	}
	return updateHelper(timer, clock, {paused: false});
};

// Pauses an existing timer. If timer was already paused, this is equivalent
// to update.
const pause = function(timer, clock) {
	if (!clock) {
		throw new Error("OpsTimer.pause requires the clock parameter");
	}
	return updateHelper(timer, clock, {paused: true});
};

// Update an existing timer to the current time without changing paused state.
const update = function(timer, clock) {
	if (!clock) {
		throw new Error("OpsTimer.update requires the clock parameter");
	}
	return updateHelper(timer, clock);
};

// Update an existing timer to the current time if at least the idle duration
// has passed since the last update. The reason this function exists is that
// too frequent updates to timers results in tons of new objects due to
// immutable updating, but too infrequent updates means large timer jumps that
// might get truncated. This function is a way to do a throttled timer update.
const updateIfPastIdle = function(timer, clock) {
	if (!clock) {
		throw new Error("OpsTimer.updateIfPastIdle requires the clock parameter");
	}
	if (clock.getActiveSince(timer.lastUpdateMs) >= clock.getIdleDuration()) {
		return updateHelper(timer, clock);
	}
	return timer;
};

// Artificially add some number of milliseconds to the timer. Mostly used for
// debugging/testing.
const addToAmount = function(timer, amountMs) {
	return setIn(timer, "amountMs", a => a + amountMs);
};

//
// Public getters that return data about the timer.
//

// Get what the clock.now() value was at the start of timer.
const getStartMs = function(timer) {
	return timer.startMs || 0;
};

// Get number of unidle and unpaused milliseconds since the start of timer.
const getAmountMs = function(timer, clock) {
	return updateAmount(timer, clock);
};

// Given a Date.now value, return an amount of milliseconds the timer would
// show if that were the current time.
const getAmountMsForNow = function(timer, now) {
	return timer.amountMs + (timer.paused ? 0 : now - timer.lastUpdateMs);
};

// Get the number of unidle and unpaused milliseconds since the last update.
const getMsSinceLast = function(timer, clock) {
	if (!clock) {
		throw new Error("OpsTimer.getMsSinceLast requires the clock parameter");
	}
	return updateAmount(timer, clock) - timer.amountMs;
};

// Returns whether the timer is paused.
const isPaused = function(timer) {
	return !!timer.paused;
};

module.exports = {
	init,
	initPaused,
	initPausedFromDBTimeSpent,
	resume,
	pause,
	update,
	updateIfPastIdle,
	addToAmount,
	getStartMs,
	getAmountMs,
	getAmountMsForNow,
	getMsSinceLast,
	isPaused,
};
